/**
 * Display helpers for the Reports v2 shell.
 *
 * Formatting only: every figure arrives computed from the engine — money as
 * `{amount, currency}` strings, rates as `{value, numerator, denominator}` — so
 * nothing here adds, divides or converts. A value the engine does not know is
 * `null` and shows as "—", never 0 (decision 8).
 */

import { formatDate, formatDateRange, formatDateTime, formatTime } from "../../services/datetime";

export const DASH = "—";

/**
 * The Intl locale for the reader's language. Arabic keeps Arabic words (month
 * names "سبتمبر", "أكتوبر" — plain `ar`, not a regional variant that says
 * "أيلول") with Western digits, so figures read the same in both languages and
 * line up in columns.
 */
export const fmtLocale = (locale) => (String(locale ?? "").startsWith("ar") ? "ar-u-nu-latn" : locale);

/**
 * The direction for a formatted value: LTR for a figure ("JOD 3,240.00",
 * "52.9%", "12:30"), its own direction as soon as it holds words — an Arabic
 * duration ("1 س 00 د"), "غير متاح", "30 يوماً" read backwards inside LTR.
 */
export const figureDir = (text) => (/[\u0600-\u06FF]/.test(String(text ?? "")) ? "auto" : "ltr");

/** Digits after the point, as the server sent them ("23.000" → 3). */
const decimalsOf = (amount) => {
  const dot = String(amount).indexOf(".");
  return dot === -1 ? 0 : String(amount).length - dot - 1;
};

/** "3240.00" → "3,240.00" in the reader's language. */
export const formatAmount = (amount, locale) => {
  if (amount === null || amount === undefined || amount === "") return DASH;

  const digits = decimalsOf(amount);

  return new Intl.NumberFormat(fmtLocale(locale), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(amount));
};

/** `{amount, currency}` → "JOD 3,240.00". */
export const formatMoney = (money, locale) => {
  if (!money || money.amount === null || money.amount === undefined) return DASH;

  return `${money.currency ?? ""} ${formatAmount(money.amount, locale)}`.trim();
};

export const formatCount = (value, locale) =>
  value === null || value === undefined ? DASH : new Intl.NumberFormat(fmtLocale(locale)).format(value);

/** A rate object or a bare number → "33.3%"; N/A when the denominator was 0. */
export const formatPercent = (rate, locale, t) => {
  if (rate === null || rate === undefined) return DASH;

  // A growth against a zero baseline is "New", never +100% (decision 4).
  if (typeof rate === "object" && rate.label === "new") return t("reports.change.new");

  const value = typeof rate === "object" ? rate.value : rate;

  if (value === null || value === undefined) return t("reports.change.na");

  return `${new Intl.NumberFormat(fmtLocale(locale), { maximumFractionDigits: 1 }).format(value)}%`;
};

/** "4 of 12" — decision 9: a rate always carries its denominator. */
export const formatDenominator = (rate, locale, t) =>
  rate && typeof rate === "object" && rate.denominator !== undefined
    ? t("reports.shell.ofDenominator", {
        numerator: formatCount(rate.numerator, locale),
        denominator: formatCount(rate.denominator, locale),
      })
    : null;

/** Minutes → "2h 05m"; negative (e.g. early by) → "−2h 05m". */
export const formatDuration = (minutes, t) => {
  if (minutes === null || minutes === undefined) return DASH;

  const whole = Math.round(minutes);
  const sign = whole < 0 ? "−" : "";
  const hours = Math.floor(Math.abs(whole) / 60);
  const rest = Math.abs(whole) % 60;

  return (
    sign +
    (hours > 0
      ? t("reports.shell.hoursMinutes", { hours, minutes: String(rest).padStart(2, "0") })
      : t("reports.shell.minutes", { minutes: rest }))
  );
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const MONTH = /^\d{4}-\d{2}$/;
const LOCAL_TIME = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}$/;

/** "2026-09-01" → "1 Sep" (a date-only value is a local day, not an instant). */
export const formatDay = (value, locale, options = {}) => {
  if (!value || !DAY.test(value)) return value ?? DASH;

  return formatDate(value, locale, { year: undefined, ...options });
};

/** A window `{from, to}` → "1 – 7 Sep 2026". */
export const formatWindow = (window, locale) =>
  window?.from && window?.to ? formatDateRange(window.from, window.to, locale) : DASH;

/**
 * The freshness line: "Updated just now" for figures computed for this
 * request (`meta.cached` false) and under a minute old, otherwise the time
 * they were computed — "Updated 10:42". The server never serves figures
 * older than the data (any change recomputes), so there is no "stale" state.
 */
export const updatedLabel = (asOf, cached, now, locale, t) => {
  if (!asOf) return "";
  if (!cached && now - new Date(asOf).getTime() < 60000) return t("reports.shell.updatedJustNow");
  return t("reports.shell.updatedAt", { time: formatTime(asOf, locale) });
};

/** An ISO instant, shown in the reporting timezone. */
export const formatInstantIn = (iso, locale, timeZone) =>
  iso ? formatDateTime(iso, locale, { timeZone: timeZone || undefined }) : DASH;

/** Any cell value by its column type. */
export const formatCell = (value, type, locale, t) => {
  if (value === null || value === undefined) return DASH;

  switch (type) {
    case "money":
      return typeof value === "object" ? formatMoney(value, locale) : formatAmount(value, locale);
    case "count":
      return formatCount(value, locale);
    case "percent":
      return formatPercent(value, locale, t);
    case "duration":
      return formatDuration(value, t);
    case "date":
      if (DAY.test(String(value))) return formatDate(value, locale);
      // A local wall-clock time in the reporting timezone ("2026-09-03 10:15"):
      // one string, never a date and a time glued together.
      if (LOCAL_TIME.test(String(value))) return formatDateTime(value, locale);

      return formatInstantIn(value, locale);
    default:
      // Day / month buckets arrive as "2026-09-01" / "2026-09".
      if (DAY.test(String(value))) return formatDay(value, locale);
      if (MONTH.test(String(value))) return formatDate(`${value}-01`, locale, { day: undefined });

      return typeof value === "object" ? JSON.stringify(value) : String(value);
  }
};

/** A KPI value of any type. */
export const formatKpiValue = (value, type, locale, t) => {
  if (value === null || value === undefined) return DASH;

  return formatCell(value, type, locale, t);
};

/** Chart point `y` → a plain number for drawing (money strings parse, unknown is a gap). */
export const plotValue = (y) => {
  if (y === null || y === undefined) return null;
  if (typeof y === "object")
    return y.amount === null || y.amount === undefined ? (y.value ?? null) : Number(y.amount);

  return Number(y);
};
